/**
 * Integrity helpers for conversation-export-v2 files.
 *
 * `canonicaliseMessages` produces a deterministic string representation of
 * a message array so that independent clients compute the same SHA-256 even
 * when JS object key ordering varies. We pin the field order + coerce
 * optional fields to stable defaults.
 *
 * `sha256Hex` runs SubtleCrypto against the canonical form and returns a
 * lowercase hex digest. Hash is purely a tamper-detection check — the
 * underlying messages are already encrypted, so this is about detecting
 * bit-flips / tampering on the exported file itself, not about secrecy.
 */

const CANONICAL_FIELDS = [
  'id',
  'sender_id',
  'encrypted_content',
  'encrypted_key_for_exporter',
  'created_at',
  'expires_at',
  'is_file',
  'reactions',
];

export function canonicaliseMessages(messages) {
  const normalised = (messages || []).map((m) => {
    const out = {};
    for (const k of CANONICAL_FIELDS) {
      // Pin a stable default so `undefined` vs `null` vs missing all hash the same.
      if (k === 'is_file') out[k] = !!m?.[k];
      else if (k === 'reactions') out[k] = m?.[k] || {};
      else out[k] = m?.[k] ?? null;
    }
    return out;
  });
  return JSON.stringify(normalised);
}

export async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function computeMessagesHash(messages) {
  return sha256Hex(canonicaliseMessages(messages));
}

// ─── Audit entries (Local Data Inspector) ───────────────────────────
// Device-state audit entries have shape `{ key, value, meta }` — we hash
// only `key` + `value` since those are the fields Restore actually writes
// back. `meta` is auto-managed on restore (stamped with `restoredAt` etc.)
// so hashing it would produce false mismatches against a freshly-exported
// file that was never modified.

const AUDIT_CANONICAL_FIELDS = ['key', 'value'];

export function canonicaliseAuditEntries(entries) {
  const normalised = (entries || []).map((e) => {
    const out = {};
    for (const k of AUDIT_CANONICAL_FIELDS) {
      out[k] = e?.[k] ?? null;
    }
    return out;
  });
  return JSON.stringify(normalised);
}

export async function computeAuditEntriesHash(entries) {
  return sha256Hex(canonicaliseAuditEntries(entries));
}
